import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import FormLayoutRenderer from '../../components/generic/FormLayoutRenderer';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { Button, buttonVariants, Label, NumberInput, Select, Switch, TextInput } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  flattenPersonalFolders,
  useAccessibleAccounts,
  useEmailTags,
  usePersonalFolders,
} from '../emails/useData';
import api from '../../services/api';
import {
  ACTION_TYPE_OPTIONS,
  CONDITION_FIELD_OPTIONS,
  CONDITION_MATCH_OPTIONS,
  CONDITION_OPERATOR_OPTIONS,
  JUNK_MAILBOX_FOLDER,
  WORKFLOW_STATUS_OPTIONS,
  moduleConfig,
} from './config';
import { useEmailRule, EMAIL_RULES_LIST_KEY, type EmailRule, type EmailRuleFormData } from './useData';

interface UserOption {
  id: number;
  userDisplayName?: string | null;
  userFirstName?: string;
  userLastName?: string;
  userEmail: string;
}

function userLabel(u: UserOption): string {
  return (
    u.userDisplayName?.trim() ||
    [u.userFirstName, u.userLastName].filter(Boolean).join(' ').trim() ||
    u.userEmail
  );
}

function normalizeCopyAction(
  action: EmailRuleFormData['actions']['actions'][number],
): { type: 'copyToFolder'; personalFolderId: number } {
  if (
    action.type === 'copyToFolder' &&
    'personalFolderId' in action &&
    typeof action.personalFolderId === 'number' &&
    action.personalFolderId > 0
  ) {
    return { type: 'copyToFolder', personalFolderId: action.personalFolderId };
  }
  return { type: 'copyToFolder', personalFolderId: 0 };
}

function actionTypeSelectValue(
  action: EmailRuleFormData['actions']['actions'][number] | undefined,
): string {
  if (action?.type === 'moveToFolder') return 'moveToJunk';
  return action?.type ?? 'copyToFolder';
}

function buildSubmitPayload(form: EmailRuleFormData): EmailRuleFormData {
  const priority =
    typeof form.priority === 'number'
      ? form.priority
      : parseInt(String(form.priority ?? 0), 10);
  return {
    ...form,
    name: form.name?.trim() ?? '',
    priority: Number.isFinite(priority) ? priority : 0,
    emailAccountId: form.emailAccountId ?? null,
  };
}

const EMAIL_RULE_FORM_ID = 'email-rule-form';

function isMoveToJunkAction(
  action: EmailRuleFormData['actions']['actions'][number] | undefined,
): boolean {
  return (
    action?.type === 'moveToFolder' &&
    action.folder?.toUpperCase() === JUNK_MAILBOX_FOLDER
  );
}

function mapExistingToForm(existing: EmailRule): EmailRuleFormData {
  const actions = {
    ...existing.actions,
    actions: existing.actions.actions.map((a) => {
      if (a.type === 'copyToFolder') {
        return normalizeCopyAction(a);
      }
      if (a.type === 'moveToFolder') {
        return a;
      }
      if ((a as { type: string }).type === 'blockSender') {
        return { type: 'moveToFolder' as const, folder: JUNK_MAILBOX_FOLDER };
      }
      return a;
    }),
  };
  return {
    name: existing.name ?? '',
    emailAccountId: existing.emailAccountId ?? null,
    enabled: existing.enabled,
    priority: existing.priority,
    conditions: existing.conditions,
    actions,
  };
}

function buildDefaultForm(): EmailRuleFormData {
  return {
    name: '',
    emailAccountId: null,
    enabled: true,
    priority: 0,
    conditions: { match: 'all', rules: [{ field: 'from', operator: 'contains', value: '' }] },
    actions: {
      actions: [{ type: 'copyToFolder', personalFolderId: 0 }],
    },
  };
}

const FormPage: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const isMobile = false;
  const ruleId = id ? parseInt(id, 10) : 0;
  const { data: existing, isLoading, isError } = useEmailRule(ruleId);
  const { data: accounts } = useAccessibleAccounts();
  const { data: personalFolders } = usePersonalFolders();
  const { data: emailTags } = useEmailTags();
  const [form, setForm] = useState<EmailRuleFormData | null>(null);
  const [formInitialized, setFormInitialized] = useState(false);
  const lastInitKeyRef = useRef<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);

  const flatFolders = useMemo(
    () => flattenPersonalFolders(personalFolders ?? []),
    [personalFolders],
  );

  const personalFolderOptions = useMemo(
    () =>
      flatFolders.map((f) => ({
        value: String(f.id),
        label: `${f.depth > 0 ? `${'— '.repeat(f.depth)}` : ''}${f.name}`,
      })),
    [flatFolders],
  );

  const defaultPersonalFolderId = flatFolders[0]?.id ?? 0;

  const tagOptions = useMemo(
    () =>
      (emailTags ?? []).map((tag) => ({
        value: tag.name,
        label: tag.name,
      })),
    [emailTags],
  );

  const defaultTagName = emailTags?.[0]?.name ?? '';

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: String(u.id),
        label: userLabel(u),
      })),
    [users],
  );

  const defaultAssignedUserId = users[0]?.id ?? 0;

  const accountOptions = useMemo(
    () =>
      (accounts ?? []).map((a) => ({
        value: String(a.id),
        label: a.displayName || a.emailAddress,
        description: a.displayName ? a.emailAddress : undefined,
      })),
    [accounts],
  );

  useEffect(() => {
    api
      .get<UserOption[]>('/users')
      .then((res) => {
        setUsers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    setForm(null);
    setFormInitialized(false);
    lastInitKeyRef.current = null;
    setError(null);
  }, [isEdit, ruleId]);

  useEffect(() => {
    const initKey = isEdit ? `edit-${ruleId}` : 'new';
    if (lastInitKeyRef.current === initKey) return;

    if (isEdit) {
      if (isLoading) return;
      if (!existing || existing.id !== ruleId) {
        lastInitKeyRef.current = initKey;
        setFormInitialized(true);
        return;
      }
      setForm(mapExistingToForm(existing));
      lastInitKeyRef.current = initKey;
      setFormInitialized(true);
      return;
    }

    setForm(buildDefaultForm());
    lastInitKeyRef.current = initKey;
    setFormInitialized(true);
  }, [isEdit, isLoading, existing, ruleId]);

  useEffect(() => {
    setForm((f) => {
      if (!f) return f;
      const action = f.actions.actions[0];
      if (!action || isMoveToJunkAction(action)) return f;

      if (action.type === 'copyToFolder') {
        if (flatFolders.length === 0) return f;
        const folderId = 'personalFolderId' in action ? action.personalFolderId : 0;
        if (folderId > 0) return f;
        return {
          ...f,
          actions: { actions: [{ type: 'copyToFolder', personalFolderId: flatFolders[0].id }] },
        };
      }

      if (action.type === 'addTag') {
        if (!emailTags?.length || action.tagName?.trim()) return f;
        return {
          ...f,
          actions: { actions: [{ type: 'addTag', tagName: emailTags[0].name }] },
        };
      }

      if (action.type === 'assignUser') {
        if (users.length === 0) return f;
        if ('assignedToUserId' in action && action.assignedToUserId > 0) return f;
        return {
          ...f,
          actions: { actions: [{ type: 'assignUser', assignedToUserId: users[0].id }] },
        };
      }

      return f;
    });
  }, [flatFolders, emailTags, users]);

  const handleChange = (name: string, value: unknown) => {
    setForm((prev) => (prev ? { ...prev, [name]: value } : prev));
    setError(null);
  };

  const formMeta = isEdit ? moduleConfig.form.edit : moduleConfig.form.new;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;

    const action = form.actions.actions[0];
    if (
      action?.type === 'copyToFolder' &&
      (!('personalFolderId' in action) || action.personalFolderId <= 0)
    ) {
      setError('Please select a personal folder.');
      return;
    }
    if (action?.type === 'addTag' && !action.tagName?.trim()) {
      setError('Please select a tag.');
      return;
    }
    if (
      action?.type === 'assignUser' &&
      (!('assignedToUserId' in action) || action.assignedToUserId <= 0)
    ) {
      setError('Please select a user to assign.');
      return;
    }
    if (
      action?.type === 'setWorkflowStatus' &&
      !action.workflowStatus
    ) {
      setError('Please select a status.');
      return;
    }

    const payload = buildSubmitPayload(form);

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await api.patch(`/email-rules/${ruleId}`, payload);
        await globalMutate(`/email-rules/${ruleId}`);
      } else {
        await api.post('/email-rules', payload);
      }
      await globalMutate(EMAIL_RULES_LIST_KEY);
      navigate(moduleConfig.module.route.list, { replace: true });
    } catch {
      setError(moduleConfig.form.errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!formInitialized || (isEdit && isLoading)) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner size="large" message={moduleConfig.form.loadingText} />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="w-full max-w-[90rem] mx-auto px-4 py-6 sm:px-6">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {isError || isEdit
            ? 'This email rule could not be loaded. It may have been deleted or you may not have access.'
            : moduleConfig.form.errorMessage}
        </div>
        <div className="mt-4 flex justify-end">
          <Link
            to={moduleConfig.module.route.list}
            className={cn(
              buttonVariants({ variant: 'outline', size: 'default' }),
              colors.surface,
              colors.text,
              'border',
              colors.border,
              `${colors.interactiveHover}`,
              'shadow-sm',
            )}
          >
            {moduleConfig.form.cancelText}
          </Link>
        </div>
      </div>
    );
  }

  const cond = form.conditions.rules[0] ?? { field: 'from', operator: 'contains', value: '' };
  const action = form.actions.actions[0];
  const copyFolderId =
    action?.type === 'copyToFolder' && 'personalFolderId' in action
      ? action.personalFolderId
      : 0;

  const renderedFields: Record<string, React.ReactNode> = {
    name: (
      <div>
        <Label htmlFor="rule-name" required>
          Name
        </Label>
        <TextInput
          id="rule-name"
          name="name"
          value={form.name ?? ''}
          placeholder="e.g. Copy newsletters to Archive"
          onChange={(e) => handleChange('name', e.target.value)}
        />
      </div>
    ),
    emailAccountId: (
      <div>
        <Label htmlFor="rule-account">Account</Label>
        <Select
          name="emailAccountId"
          value={form.emailAccountId != null ? String(form.emailAccountId) : ''}
          placeholder="All accounts"
          options={accountOptions}
          showClearButton
          helperText="Leave empty to apply across all accessible accounts."
          onChange={(e) =>
            handleChange(
              'emailAccountId',
              e.target.value ? parseInt(e.target.value, 10) : null,
            )
          }
        />
      </div>
    ),
    priority: (
      <div>
        <Label htmlFor="rule-priority">Priority</Label>
        <NumberInput
          id="rule-priority"
          name="priority"
          value={form.priority}
          min={0}
          step={1}
          allowDecimals={false}
          helperText="Lower numbers run first."
          onChange={(e) => handleChange('priority', e.target.value ?? 0)}
        />
      </div>
    ),
    enabled: (
      <div className="pt-6">
        <Switch
          name="enabled"
          checked={form.enabled}
          label="Enabled"
          description="Disabled rules are kept but not applied."
          labelPosition="left"
          onChange={(e) => handleChange('enabled', e.target.checked)}
        />
      </div>
    ),
    conditions: (
      <div className="space-y-4">
        <div>
          <Label htmlFor="conditionMatch">Match mode</Label>
          <Select
            name="conditionMatch"
            value={form.conditions.match}
            options={CONDITION_MATCH_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            showClearButton={false}
            onChange={(e) =>
              setForm((f) =>
                f
                  ? {
                      ...f,
                      conditions: {
                        ...f.conditions,
                        match: e.target.value as 'all' | 'any',
                      },
                    }
                  : f,
              )
            }
          />
        </div>
        <div className={cn('rounded-lg border p-4 space-y-4', colors.border, colors.surfaceHover)}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="conditionField">Field</Label>
              <Select
                name="conditionField"
                value={cond.field}
                options={CONDITION_FIELD_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                showClearButton={false}
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          conditions: {
                            ...f.conditions,
                            rules: [
                              {
                                ...cond,
                                field: e.target.value as typeof cond.field,
                              },
                            ],
                          },
                        }
                      : f,
                  )
                }
              />
            </div>
            <div>
              <Label htmlFor="conditionOperator">Operator</Label>
              <Select
                name="conditionOperator"
                value={cond.operator}
                options={CONDITION_OPERATOR_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                showClearButton={false}
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          conditions: {
                            ...f.conditions,
                            rules: [
                              {
                                ...cond,
                                operator: e.target.value as typeof cond.operator,
                              },
                            ],
                          },
                        }
                      : f,
                  )
                }
              />
            </div>
            <div>
              <Label htmlFor="conditionValue" required>
                Value
              </Label>
              <TextInput
                id="conditionValue"
                name="conditionValue"
                value={cond.value}
                placeholder="e.g. newsletter@example.com"
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          conditions: {
                            ...f.conditions,
                            rules: [{ ...cond, value: e.target.value }],
                          },
                        }
                      : f,
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>
    ),
    actions: (
      <div className="space-y-4">
        <div>
          <Label htmlFor="actionType">Action</Label>
          <Select
            name="actionType"
            value={actionTypeSelectValue(action)}
            options={ACTION_TYPE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            showClearButton={false}
            onChange={(e) => {
              const t = e.target.value;
              if (t === 'markRead') {
                setForm((f) => (f ? { ...f, actions: { actions: [{ type: 'markRead' }] } } : f));
              } else if (t === 'addTag') {
                setForm((f) =>
                  f
                    ? {
                        ...f,
                        actions: { actions: [{ type: 'addTag', tagName: defaultTagName }] },
                      }
                    : f,
                );
              } else if (t === 'moveToJunk') {
                setForm((f) =>
                  f
                    ? {
                        ...f,
                        actions: {
                          actions: [{ type: 'moveToFolder', folder: JUNK_MAILBOX_FOLDER }],
                        },
                      }
                    : f,
                );
              } else if (t === 'assignUser') {
                setForm((f) =>
                  f
                    ? {
                        ...f,
                        actions: {
                          actions: [
                            {
                              type: 'assignUser',
                              assignedToUserId: defaultAssignedUserId,
                            },
                          ],
                        },
                      }
                    : f,
                );
              } else if (t === 'setWorkflowStatus') {
                setForm((f) =>
                  f
                    ? {
                        ...f,
                        actions: {
                          actions: [
                            {
                              type: 'setWorkflowStatus',
                              workflowStatus: 'open',
                            },
                          ],
                        },
                      }
                    : f,
                );
              } else {
                setForm((f) =>
                  f
                    ? {
                        ...f,
                        actions: {
                          actions: [
                            {
                              type: 'copyToFolder',
                              personalFolderId: defaultPersonalFolderId,
                            },
                          ],
                        },
                      }
                    : f,
                );
              }
            }}
          />
        </div>

        {isMoveToJunkAction(action) && (
          <p className={cn('text-sm', colors.textMuted)}>
            Matching emails are moved to the account&apos;s Junk folder (same as marking spam).
            {form.emailAccountId == null && (
              <> Applies per matching message&apos;s own account when the rule is set to all accounts.</>
            )}
          </p>
        )}

        {action?.type === 'copyToFolder' && (
          <div className={cn('rounded-lg border p-4', colors.border, colors.surfaceHover)}>
            <Label htmlFor="actionPersonalFolder" required>
              Personal folder
            </Label>
            {personalFolderOptions.length === 0 ? (
              <p className={cn('text-sm mt-2', colors.textMuted)}>
                No personal folders yet.{' '}
                <Link to="/emails" className={cn('underline', colors.primaryFg)}>
                  Create one in the mail sidebar
                </Link>
                .
              </p>
            ) : (
              <Select
                name="actionPersonalFolder"
                value={copyFolderId > 0 ? String(copyFolderId) : ''}
                placeholder="Select a folder"
                options={personalFolderOptions}
                showClearButton={false}
                helperText="Matching emails are added to this folder without leaving their original location."
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          actions: {
                            actions: [
                              {
                                type: 'copyToFolder',
                                personalFolderId: parseInt(e.target.value, 10),
                              },
                            ],
                          },
                        }
                      : f,
                  )
                }
              />
            )}
          </div>
        )}

        {action?.type === 'addTag' && (
          <div className={cn('rounded-lg border p-4', colors.border, colors.surfaceHover)}>
            <Label htmlFor="actionTag" required>
              Tag
            </Label>
            {tagOptions.length === 0 ? (
              <p className={cn('text-sm mt-2', colors.textMuted)}>
                No tags yet.{' '}
                <Link to="/emails" className={cn('underline', colors.primaryFg)}>
                  Create one in the mail sidebar
                </Link>
                .
              </p>
            ) : (
              <Select
                name="actionTag"
                value={action.tagName ?? ''}
                placeholder="Select a tag"
                options={tagOptions}
                showClearButton={false}
                helperText="Matching emails receive this tag after sync."
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          actions: { actions: [{ type: 'addTag', tagName: e.target.value }] },
                        }
                      : f,
                  )
                }
              />
            )}
          </div>
        )}

        {action?.type === 'assignUser' && (
          <div className={cn('rounded-lg border p-4', colors.border, colors.surfaceHover)}>
            <Label htmlFor="actionAssignUser" required>
              Assign to
            </Label>
            {userOptions.length === 0 ? (
              <p className={cn('text-sm mt-2', colors.textMuted)}>Loading users…</p>
            ) : (
              <Select
                name="actionAssignUser"
                value={
                  action.assignedToUserId > 0 ? String(action.assignedToUserId) : ''
                }
                placeholder="Select a user"
                options={userOptions}
                showClearButton={false}
                searchable={userOptions.length > 8}
                helperText="Matching emails are assigned to this user with status Open."
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          actions: {
                            actions: [
                              {
                                type: 'assignUser',
                                assignedToUserId: parseInt(e.target.value, 10),
                              },
                            ],
                          },
                        }
                      : f,
                  )
                }
              />
            )}
          </div>
        )}

        {action?.type === 'setWorkflowStatus' && (
          <div className={cn('rounded-lg border p-4', colors.border, colors.surfaceHover)}>
            <Label htmlFor="actionWorkflowStatus" required>
              Status
            </Label>
            <Select
              name="actionWorkflowStatus"
              value={action.workflowStatus ?? 'open'}
              options={WORKFLOW_STATUS_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              showClearButton={false}
              helperText="Only applies when the email already has an assignee (e.g. after an Assign rule in the same sync)."
              onChange={(e) =>
                setForm((f) =>
                  f
                    ? {
                        ...f,
                        actions: {
                          actions: [
                            {
                              type: 'setWorkflowStatus',
                              workflowStatus: e.target.value as 'open' | 'working' | 'closed',
                            },
                          ],
                        },
                      }
                    : f,
                )
              }
            />
          </div>
        )}
      </div>
    ),
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div
        className={cn(
          isMobile ? 'w-full px-2' : 'max-w-[90rem] mx-auto w-full',
          'flex flex-col h-full min-h-0',
        )}
      >
        {!isEdit && (
          <div className={cn('flex-shrink-0', isMobile ? 'mb-2' : 'mb-3')}>
            <h1 className={cn(isMobile ? 'text-xl' : 'text-2xl', 'font-bold', colors.text)}>
              {formMeta.title}
            </h1>
            {formMeta.subtitle && (
              <p className={cn(colors.textSecondary, 'mt-0.5', isMobile ? 'text-xs' : 'text-sm')}>
                {formMeta.subtitle}
              </p>
            )}
          </div>
        )}

        {error && (
          <div
            className={cn(
              'flex-shrink-0 mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5',
              isMobile && 'mb-3 px-3 py-2',
            )}
          >
            <p className={cn('text-sm text-red-800', isMobile && 'text-xs')}>{error}</p>
          </div>
        )}

        <form
          id={EMAIL_RULE_FORM_ID}
          onSubmit={handleSubmit}
          className={cn(
            'flex-1 flex flex-col min-h-0',
            colors.surface,
            'rounded-lg shadow-sm border',
            colors.border,
            'overflow-hidden',
          )}
        >
          <div
            className={cn(
              'flex-1 min-h-0 overflow-y-auto dashboard-scrollbar',
              isMobile ? 'px-3 pt-3 pb-3' : 'px-5 pt-4 pb-4',
            )}
          >
            <FormLayoutRenderer
              layout={moduleConfig.formLayout}
              fields={renderedFields}
              formData={form as unknown as Record<string, unknown>}
              isMobile={isMobile}
            />
          </div>

          <div
            className={cn(
              'flex-shrink-0 flex',
              isMobile ? 'flex-col-reverse gap-2' : 'items-center justify-end space-x-3',
              isMobile ? 'p-4' : 'p-6',
              'border-t',
              colors.border,
              colors.surfaceHover,
              isMobile && 'sticky bottom-0 z-10 shadow-lg',
            )}
          >
            <Link
              to={moduleConfig.module.route.list}
              className={cn(
                buttonVariants({ variant: 'outline', size: 'default' }),
                colors.surface,
                colors.text,
                'border',
                colors.border,
                `${colors.interactiveHover}`,
                'shadow-sm',
                isMobile ? 'w-full min-h-[44px]' : '',
              )}
            >
              {moduleConfig.form.cancelText}
            </Link>
            <Button
              type="submit"
              disabled={saving}
              loading={saving}
              className={isMobile ? 'w-full min-h-[44px]' : ''}
            >
              {formMeta.submitText}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormPage;
